"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type Dispatch,
	type RefObject,
	type SetStateAction,
} from "react";
import { ChevronDown, Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { Navbar, News, Page } from "@/payload-types";
import { getPagePath } from "@/utilities/getPagePath";
import { resolveReferenceHref } from "@/utilities/resolveReferenceHref";

type NavbarSectionClientProps = {
	navbar: Navbar | null;
};

type ActionVariant = "default" | "outline" | "secondary";

type NavbarLink = NonNullable<Navbar["menuItems"]>[number]["link"];

function resolveHref(link?: NavbarLink | null) {
	if (!link) return null;
	if (link.type === "custom") return link.url?.trim() || null;

	if (link.type === "reference" && link.reference) {
		const { relationTo, value } = link.reference;

		if (relationTo === "forms") {
			return resolveReferenceHref({ relationTo: "forms", value });
		}

		if (typeof value === "string" || typeof value === "number") return null;
		if (!value || typeof value !== "object") return null;

		if (relationTo === "news") {
			const slug = (value as News).slug?.trim();
			return slug ? `/news/${slug}` : null;
		}

		return getPagePath((value as Page).slug);
	}

	return null;
}

function NavMenuItems({
	items,
	navMenuRef,
	openDropdown,
	setOpenDropdown,
	onNavLinkClick,
}: {
	items: NonNullable<Navbar["menuItems"]>;
	navMenuRef: RefObject<HTMLDivElement | null>;
	openDropdown: string | null;
	setOpenDropdown: Dispatch<SetStateAction<string | null>>;
	onNavLinkClick?: (href: string) => void;
}) {
	return (
		<div className="-mx-2 flex flex-col gap-1 xl:flex-row" ref={navMenuRef}>
			{items.map((item, index) => {
				const itemLabel = item.hasSubmenu
					? item.label?.trim()
					: item.link?.label?.trim();
				const key = item.id ?? `${itemLabel}-${index}`;
				const hasSubmenu = Boolean(item.hasSubmenu && item.submenu?.length);
				const href = resolveHref(item.link);

				if (hasSubmenu) {
					if (!itemLabel) return null;

					return (
						<div className="relative" key={key}>
							<div className="w-full">
								<Button
									className="w-full justify-between px-2 xl:w-auto"
									onClick={() =>
										setOpenDropdown(openDropdown === key ? null : key)
									}
									type="button"
									colorMode="dark"
									variant="ghost"
								>
									{itemLabel}
									<ChevronDown
										className={`transition-transform duration-200 ${openDropdown === key ? "rotate-180" : ""}`}
									/>
								</Button>
								{openDropdown === key ? (
									<div className="animate-in fade-in z-50 w-full border border-white/20 bg-black p-2 duration-200 xl:absolute xl:top-full xl:min-w-[200px] xl:shadow-lg">
										{item.submenu?.map((subItem, subIndex) => {
											const subHref = resolveHref(subItem.link);
											const subLabel = subItem.link?.label?.trim();
											if (!subHref || !subLabel) return null;

											return (
												<Button
													asChild
													colorMode="dark"
													className="w-full justify-start px-3 py-2"
													key={subItem.id ?? `${subLabel}-${subIndex}`}
													variant="ghost"
												>
													<Link
														href={subHref}
														scroll
														onClick={() => onNavLinkClick?.(subHref)}
													>
														{subLabel}
													</Link>
												</Button>
											);
										})}
									</div>
								) : null}
							</div>
						</div>
					);
				}

				if (!href) return null;
				if (!itemLabel) return null;

				return (
					<div className="relative" key={key}>
						<Button
							asChild
							className="w-full justify-start px-3 xl:w-auto xl:px-3"
							variant="ghost"
							colorMode="dark"
						>
							<Link href={href} scroll onClick={() => onNavLinkClick?.(href)}>
								{itemLabel}
							</Link>
						</Button>
					</div>
				);
			})}
		</div>
	);
}

function NavActionButtons({
	actionButtons,
	className,
	onLinkClick,
}: {
	actionButtons: NonNullable<Navbar["actionButtons"]>;
	className?: string;
	onLinkClick?: (href: string) => void;
}) {
	return (
		<div className={`flex items-center gap-2 ${className ?? ""}`}>
			{actionButtons.map((button, index) => {
				const href = resolveHref(button.link);
				const label = button.link?.label?.trim();
				if (!href || !label) return null;

				const variant = (button.variant ?? "default") as ActionVariant;
				return (
					<Button
						asChild
						className={className?.includes("w-full") ? "w-full" : ""}
						colorMode="gold"
						key={button.id ?? `${label}-${index}`}
						variant={variant}
					>
						<Link href={href} scroll onClick={() => onLinkClick?.(href)}>
							{label}
						</Link>
					</Button>
				);
			})}
		</div>
	);
}

/** Mobile Safari often emits a scroll event on tap; ignore collapse logic briefly after toggling. */
const MOBILE_MENU_SCROLL_GRACE_MS = 380;

export function NavbarSectionClient({ navbar }: NavbarSectionClientProps) {
	const pathname = usePathname();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [openDropdown, setOpenDropdown] = useState<string | null>(null);
	const lastScrollY = useRef(0);
	const navMenuRef = useRef<HTMLDivElement | null>(null);
	const skipScrollNavEffectsUntilRef = useRef(0);
	const previousPathnameRef = useRef(pathname);

	useEffect(() => {
		if (!openDropdown) return;

		const onPointerDown = (event: MouseEvent) => {
			if (!navMenuRef.current?.contains(event.target as Node)) {
				setOpenDropdown(null);
			}
		};

		document.addEventListener("mousedown", onPointerDown);

		return () => {
			document.removeEventListener("mousedown", onPointerDown);
		};
	}, [openDropdown]);

	useEffect(() => {
		const onScroll = () => {
			const currentScrollY = window.scrollY;

			if (Date.now() < skipScrollNavEffectsUntilRef.current) {
				lastScrollY.current = currentScrollY;
				return;
			}

			if (openDropdown) {
				setOpenDropdown(null);
			}

			if (currentScrollY > lastScrollY.current) {
				setIsMenuOpen(false);
			}

			lastScrollY.current = currentScrollY;
		};

		lastScrollY.current = window.scrollY;
		window.addEventListener("scroll", onScroll, { passive: true });

		return () => {
			window.removeEventListener("scroll", onScroll);
		};
	}, [openDropdown]);

	useEffect(() => {
		if (previousPathnameRef.current === pathname) return;

		previousPathnameRef.current = pathname;
		skipScrollNavEffectsUntilRef.current =
			Date.now() + MOBILE_MENU_SCROLL_GRACE_MS;

		requestAnimationFrame(() => {
			setIsMenuOpen(false);
			setOpenDropdown(null);
			lastScrollY.current = window.scrollY;
		});
	}, [pathname]);

	const closeMobileMenu = useCallback(() => {
		skipScrollNavEffectsUntilRef.current =
			Date.now() + MOBILE_MENU_SCROLL_GRACE_MS;
		setIsMenuOpen(false);
		setOpenDropdown(null);
	}, []);

	const handleInternalNavClick = useCallback(
		(href: string) => {
			closeMobileMenu();

			if (href !== "/" || pathname !== "/") return;

			window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
			lastScrollY.current = 0;
		},
		[closeMobileMenu, pathname],
	);

	if (!navbar) return null;

	const menuItems = navbar.menuItems ?? [];
	const actionButtons = navbar.actionButtons ?? [];

	return (
		<nav
			data-site-header
			className="sticky top-0 isolate z-50 border-b border-white/10 bg-black py-3.5 text-white xl:py-4"
		>
			<div className="relative container m-auto flex flex-col justify-between gap-4 px-6 xl:flex-row xl:items-center xl:gap-6">
				<div className="flex items-center justify-between xl:justify-start xl:gap-8">
					<div className="flex items-center gap-4 xl:gap-8">
						<Link
							aria-label="Go to homepage"
							className="inline-flex shrink-0 text-white"
							href="/"
							scroll
							onClick={() => handleInternalNavClick("/")}
						>
							<Image
								alt="Brooklyn 500"
								className="h-10 w-auto max-w-[min(100%,280px)] object-contain xl:h-16"
								height={80}
								priority
								src="/brooklyn-brewery-logo.png"
								width={280}
							/>
						</Link>
						<Image
							alt="Regnbågsfonden"
							className="h-10 w-auto max-w-[min(100%,280px)] object-contain xl:h-14"
							height={80}
							priority
							src="/regnbagsfonden.svg"
							width={280}
						/>
					</div>
					<Button
						aria-label={isMenuOpen ? "Close menu" : "Open menu"}
						className="flex size-9 items-center justify-center xl:hidden"
						onClick={() => {
							skipScrollNavEffectsUntilRef.current =
								Date.now() + MOBILE_MENU_SCROLL_GRACE_MS;
							setIsMenuOpen((prev) => !prev);
							setOpenDropdown(null);
						}}
						type="button"
						colorMode="dark"
						variant="ghost"
					>
						{isMenuOpen ? <X /> : <Menu />}
					</Button>
				</div>

				<div className="hidden w-full flex-row justify-end gap-5 xl:flex">
					<NavMenuItems
						items={menuItems}
						navMenuRef={navMenuRef}
						openDropdown={openDropdown}
						setOpenDropdown={setOpenDropdown}
						onNavLinkClick={handleInternalNavClick}
					/>
					{actionButtons.length > 0 ? (
						<NavActionButtons
							actionButtons={actionButtons}
							onLinkClick={handleInternalNavClick}
						/>
					) : null}
				</div>

				{isMenuOpen ? (
					<div className="flex w-full flex-col justify-end gap-5 pb-2.5 xl:hidden">
						<NavMenuItems
							items={menuItems}
							navMenuRef={navMenuRef}
							openDropdown={openDropdown}
							setOpenDropdown={setOpenDropdown}
							onNavLinkClick={handleInternalNavClick}
						/>
						{actionButtons.length > 0 ? (
							<NavActionButtons
								actionButtons={actionButtons}
								className="w-full flex-col items-stretch gap-2"
								onLinkClick={handleInternalNavClick}
							/>
						) : null}
					</div>
				) : null}
			</div>
		</nav>
	);
}
